import React, { useMemo, useCallback, memo, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { useDispatch, useSelector, batch } from "react-redux";
import { Switch, Route, Redirect, useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Wizard } from "../../../../components";
import {
  buildNewRecipientList,
  validateMapPolygon,
  saveNewRecipientList,
  geoFilterMasterRecipients,
  getMasterListHeaders,
} from "../../../../actions/newRecipientList";
import RecipientMap from "./RecipientMap";
import Save from "../Save";
import { notify } from "../../../../actions/notification";

const useStyles = makeStyles((theme) => ({
  wizard: {
    // marginTop: "auto",
  },
}));

const getSteps = ({ t, validatePolygon }) => [
  {
    label: t("Filter Map"),
    path: "/recipient-lists/create/map/filter",
    validate: validatePolygon,
    component: RecipientMap,
  },
  {
    label: t("Save"),
    path: "/recipient-lists/create/map/save",
    component: Save,
  },
];

function FilterRecipientListFromMap({ onAbort, afterSave, children }) {
  const dispatch = useDispatch();
  const history = useHistory();
  const newRecipientList = useSelector((state) => state.newRecipientList);
  const mapState = useSelector((state) => state.map);
  const { doneStepIdx } = newRecipientList;

  const classes = useStyles();
  const { t } = useTranslation();

  const validatePolygon = useCallback(() => dispatch(validateMapPolygon()), [
    dispatch,
  ]);

  const steps = useMemo(() => getSteps({ t, validatePolygon }), [
    t,
    validatePolygon
  ]);

  const onStepChange = useCallback(
    (activeStepIdx) => {
      dispatch(buildNewRecipientList({
        activeStepIdx,
        doneStepIdx: activeStepIdx > 0 ? Math.max(0, doneStepIdx) : doneStepIdx,
        errors: {}
      }));
    },
    [dispatch, doneStepIdx]
  );

  const onSave = useCallback(async () => {
    try {
      dispatch(buildNewRecipientList({ loading: true }));

      const { 
        withinPolygon,
        totalCount,
        failedCount,
        emailCount,
        smsCount,
        voiceCount,
        ttyCount,
        customCount
      } = newRecipientList;

      const recipientListId = await dispatch(saveNewRecipientList());
        
      if (recipientListId) {
        dispatch(buildNewRecipientList({ 
          withinPolygon,
          totalCount,
          failedCount,
          emailCount,
          smsCount,
          voiceCount,
          ttyCount,
          customCount
        }));
        
        // Apply the geo filter to filter recipients within the polygon
        await dispatch(geoFilterMasterRecipients());
          
        batch(() => {
          if (afterSave) {
            afterSave(recipientListId);
          }
        });
      }
    } catch (error) {
      dispatch(buildNewRecipientList({
        loading: false,
        errors: { general: error?.response?.data?.message || 'Failed to save recipient list' }
      }));
      dispatch(notify({
        type: "error",
        message: "Save operation failed: " + (error?.response?.data?.message || error.message || "Unknown error")
      }));
    }
  }, [dispatch, afterSave, newRecipientList]);
  
  // Get master list data when component mounts
  useEffect(() => {
    dispatch(getMasterListHeaders());
  }, [dispatch]);

  // Validate map whenever shapes or layers change
  useEffect(() => {
    if (mapState && !newRecipientList.loading) {
      dispatch(validateMapPolygon());
    }
  }, [dispatch, mapState?.shapes, mapState?.enabledLayers, newRecipientList.loading]);

  return (
    <>
      <Wizard
        steps={steps}
        className={classes.wizard}
        doneStepIdx={doneStepIdx}
        onStepChange={onStepChange}
        disableNext={
          Object.keys(newRecipientList.errors).length ||
          newRecipientList.loading
        }
        errors={newRecipientList.errors}
        onSave={onSave}
        onAbort={onAbort}
      />
      {children}
      <Switch>
        {steps.slice(0, doneStepIdx + 2).map((step) => (
          <Route key={step.path} path={step.path} component={step.component} />
        ))}
        <Redirect to={steps[Math.min(doneStepIdx + 1, steps.length - 1)].path} />
      </Switch>
    </>
  );
}

export default memo(FilterRecipientListFromMap);
